import { useMemo, useState } from 'react';
import { Pressable } from 'react-native';
import { twJoin } from 'tailwind-merge';

import { DisclosureSection } from '@/components/account/DisclosureSection';
import { Divider } from '@/components/ui/divider';
import { Input, InputField } from '@/components/ui/input';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { useAllFeatureFlags, type ResolvedFeatureFlag } from '@/hooks/useFeatureFlag';
import { getAccessibilityProps } from '@/util/accessibilityProps';
import { getNativeTextProps } from '@/util/getNativeTextProps';

/** A flag's value, shown only when it says more than the flag being on. */
export function describeFlagValue({ value }: ResolvedFeatureFlag): string | null {
  if (typeof value === 'boolean') {
    return null;
  }

  return typeof value === 'string' ? value : JSON.stringify(value);
}

/**
 * The flags that are on, by name, narrowed to those whose name or value contains `query`, ignoring
 * case. Searching values as well finds a flag by what it targets, such as a user id in a roster.
 */
export function filterActiveFlags(
  flags: readonly ResolvedFeatureFlag[],
  query: string
): ResolvedFeatureFlag[] {
  const needle = query.trim().toLowerCase();

  return flags
    .filter((flag) => flag.enabled)
    .filter(
      (flag) =>
        !needle ||
        flag.flag.toLowerCase().includes(needle) ||
        (describeFlagValue(flag)?.toLowerCase().includes(needle) ?? false)
    )
    .sort((first, second) => first.flag.localeCompare(second.flag));
}

/**
 * Every flag that is on for the signed-in user, resolved as the app resolves it, so a targeted flag
 * can be checked without opening Flagsmith.
 *
 * Folded into a disclosure section that counts the flags that are on out of the total, since the
 * list runs to dozens and pushed everything after it off the screen. Opened, it offers a search over
 * names and values, and each value shows one line until tapped.
 */
export function ActiveFeatureFlags() {
  const flags = useAllFeatureFlags();

  const [query, setQuery] = useState('');

  const [expandedFlag, setExpandedFlag] = useState<string | null>(null);

  const onCount = flags.filter((flag) => flag.enabled).length;

  const visible = useMemo(() => filterActiveFlags(flags, query), [flags, query]);

  const title = `Feature flags (${onCount}/${flags.length})`;

  /** The short count reads as a fraction, so a screen reader says what it counts. */
  const spokenTitle = `Feature flags, ${onCount} of ${flags.length} on`;

  return (
    <DisclosureSection title={title} accessibilityLabel={spokenTitle}>
      <Text size="xs" className="text-typography-900">
        What this session sees after Flagsmith, test overrides and defaults.
      </Text>

      <Input size="sm">
        <InputField
          placeholder="Search flags"
          value={query}
          onChangeText={setQuery}
          autoCapitalize="none"
          autoCorrect={false}
          accessibilityLabel="Search flags"
        />
      </Input>

      {visible.length === 0 ? (
        <Text size="sm" className="text-typography-700">
          {query.trim() ? 'No flags match.' : 'No flags are on.'}
        </Text>
      ) : (
        <VStack>
          {visible.map((flag, index) => {
            const value = describeFlagValue(flag);

            const isExpanded = expandedFlag === flag.flag;

            const row = (
              <VStack className="gap-1 py-3">
                <Text size="sm" className="font-medium font-mono text-typography-950">
                  {flag.flag}
                </Text>

                {value !== null ? (
                  <Text
                    size="sm"
                    className={twJoin(
                      'break-all text-typography-700',
                      !isExpanded && 'web:line-clamp-1'
                    )}
                    {...(isExpanded ? {} : getNativeTextProps({ numberOfLines: 1 }))}>
                    {value}
                  </Text>
                ) : null}
              </VStack>
            );

            const item =
              value === null ? (
                row
              ) : (
                <Pressable
                  onPress={() => setExpandedFlag(isExpanded ? null : flag.flag)}
                  accessibilityState={{ expanded: isExpanded }}
                  {...getAccessibilityProps({
                    role: 'button',
                    label: flag.flag,
                    hint: isExpanded ? 'Shows one line of the value' : 'Shows the whole value',
                  })}>
                  {row}
                </Pressable>
              );

            return (
              <VStack key={flag.flag}>
                {index > 0 ? <Divider /> : null}

                {item}
              </VStack>
            );
          })}
        </VStack>
      )}
    </DisclosureSection>
  );
}
